import React from "react";
import { format } from "date-fns";
import {
  IonItem,
  IonLabel,
  IonButton,
  IonIcon,
  IonText,
  IonAccordion,
  IonAccordionGroup,
  IonList,
  useIonAlert,
} from "@ionic/react";
import { navigate, createOutline, layersOutline, trashOutline } from "ionicons/icons";
import { generateMapUrls, parseNotes2 } from "../../functions/FX_ParseAndMap";
import { SchedJobData } from "../../hooks/scheduler/useJobData";

interface Props {
  jobDocId: string;
  schedJob: SchedJobData;
  onEdit: () => void;
  onClone: () => void;
  onDelete: () => void;
  onMarkPaid?: () => void;
  onUndoPaid?: () => void;

  // keep these for compatibility if you still pass them separately anywhere
  location?: string;
  locationCoordinates?: string;
  shiftNotes?: string;
}

export const SchedDetailsComponent: React.FC<Props> = ({
  jobDocId,
  schedJob,
  onEdit,
  onClone,
  onDelete,
  onMarkPaid,
  onUndoPaid,
  location,
  locationCoordinates,
  shiftNotes,
}) => {
  const [present] = useIonAlert();

  // Prefer props if provided, fallback to schedJob
  const resolvedLocation = typeof location === "string" ? location : schedJob?.Location ?? "";
  const resolvedCoords = typeof locationCoordinates === "string" ? locationCoordinates : schedJob?.LocationCoordinates ?? "";
  const resolvedNotes = typeof shiftNotes === "string" ? shiftNotes : schedJob?.ShiftNotes ?? "";

  const hasNotes = typeof resolvedNotes === "string" && resolvedNotes.trim().length > 0;
  const hasCoordText = typeof resolvedCoords === "string" && resolvedCoords.trim().length > 0;

  const SectionLabelTight: React.FC<{ text: string; mt?: number }> = ({ text, mt = 10 }) => (
    <div
      style={{
        marginTop: mt,
        marginBottom: 4,
        paddingLeft: 16,
        paddingRight: 16,
        fontSize: "0.8rem",
        letterSpacing: "0.03em",
        opacity: 0.65,
        textTransform: "uppercase",
        fontWeight: 600,
      }}
    >
      {text}
    </div>
  );

  const SoftDivider: React.FC<{ mt?: number; mb?: number }> = ({ mt = 12, mb = 10 }) => (
    <div
      style={{
        height: 1,
        opacity: 0.12,
        margin: `${mt}px 16px ${mb}px 16px`,
        background: "currentColor",
      }}
    />
  );

  const getSeriesLabel = (): string | null => {
    if (!schedJob?.seriesId) return null;

    if (
      Number.isFinite(schedJob.seriesIndex) &&
      Number.isFinite(schedJob.seriesTotal) &&
      schedJob.seriesTotal != null &&
      schedJob.seriesTotal > 0
    ) {
      return `Series ${schedJob.seriesIndex}/${schedJob.seriesTotal}`;
    }

    return "Series";
  };

  const getHoursLabel = () => {
    try {
      const start = new Date(schedJob.StartDateTimeRaw);
      const end = new Date(schedJob.FinishDateTimeRaw);
      const mins = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
      const hrs = mins / 60;
      const pretty = Number.isInteger(hrs) ? `${hrs}` : `${Math.round(hrs * 10) / 10}`;
      return `${pretty} hrs`;
    } catch {
      return "";
    }
  };

  const openInGoogleMaps = () => {
    const { googleMapsUrl } = generateMapUrls(resolvedCoords);
    window.open(googleMapsUrl, "_blank");
  };

  const openInAppleMaps = () => {
    if (resolvedCoords.startsWith("https://maps.apple.com/") || resolvedCoords.startsWith("maps:")) {
      window.open(resolvedCoords);
    } else {
      const { appleMapsUrl } = generateMapUrls(resolvedCoords);
      window.open(appleMapsUrl);
    }
  };

  const NotesSection: React.FC = () => {
    const defaultValue = hasNotes ? "notes" : undefined;

    return (
      <IonAccordionGroup
        value={defaultValue}
      >
        <IonAccordion
          value="notes"
        >
          <IonItem
            slot="header"
            lines="none"
            button={true}
          >
            <IonLabel
              style={{
                fontSize: "0.8rem",
                letterSpacing: "0.03em",
                opacity: 0.65,
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              Notes
            </IonLabel>
          </IonItem>

          <div slot="content" style={{ padding: "10px 16px 6px 16px" }}>
            {hasNotes ? (
              <IonText
                style={{ opacity: 0.88, fontSize: "0.98rem" }}
              >
                {parseNotes2(resolvedNotes)}
              </IonText>
            ) : (
              <IonText
                style={{ opacity: 0.55, fontSize: "0.95rem" }}
              >
                No notes.
              </IonText>
            )}
          </div>
        </IonAccordion>
      </IonAccordionGroup>
    );
  };

  // ---------------------------
  // Core Summary (moved INTO Details tab)
  // ---------------------------
  const CoreSummary: React.FC = () => {
    const startRaw = schedJob.StartDateTimeRaw;
    const endRaw = schedJob.FinishDateTimeRaw;

    const start = startRaw ? new Date(startRaw) : null;
    const end = endRaw ? new Date(endRaw) : null;

    const startOk = !!start && Number.isFinite((start as Date).getTime());
    const endOk = !!end && Number.isFinite((end as Date).getTime());

    // Check whether the shift spans multiple calendar days
    const sameDay =
      startOk && endOk &&
      (start as Date).getFullYear() === (end as Date).getFullYear() &&
      (start as Date).getMonth() === (end as Date).getMonth() &&
      (start as Date).getDate() === (end as Date).getDate();

    const hoursLabel = startOk && endOk ? getHoursLabel() : "";

    const filled = schedJob.scheduledCount ?? 0;
    const total = schedJob.NumJobSlots ?? 0;

    const seriesLabel = getSeriesLabel();

    return (
      <IonList
        lines="inset"
      >
        {/* Company + shift id + series */}
        <IonItem
          lines="none"
        >
          <IonLabel style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, opacity: 0.92, whiteSpace: "normal" }}>{schedJob.Company}</div>

            <div style={{ marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap", opacity: 0.72 }}>
              <span style={{ fontSize: "0.92rem" }}>{schedJob.ShiftIdentifier}</span>
              {seriesLabel && (
                <IonText
                  style={{ fontSize: "0.85rem", fontWeight: 700, letterSpacing: "0.03em", opacity: 0.85 }}
                >
                  <span>{seriesLabel}</span>
                </IonText>
              )}
            </div>
          </IonLabel>
        </IonItem>

        {/* Date + time */}
        <IonItem lines="none">
          <IonLabel>
            {sameDay ? (
              <>
                <div style={{ fontWeight: 550, opacity: 0.92 }}>
                  {startOk ? format(start as Date, "EEE • MMM d, yy") : "—"}
                </div>
                <div
                  style={{
                    marginTop: 4,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 14,
                    opacity: 0.86,
                    fontSize: "0.96rem",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    {startOk && endOk
                      ? `${format(start as Date, "h:mma")} – ${format(end as Date, "h:mma")}`
                      : "—"}{" "}
                    • {hoursLabel}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 450, opacity: 0.92, fontSize: "0.95rem" }}>
                  <span style={{ fontSize: "0.75rem", opacity: 0.55, textTransform: "uppercase", letterSpacing: "0.04em", marginRight: 6 }}>Start</span>
                  {startOk ? `${format(start as Date, "EEE • MMM d, yy")} • ${format(start as Date, "h:mma")}` : "—"}
                </div>
                <div style={{ fontWeight: 450, opacity: 0.92, fontSize: "0.95rem", marginTop: 3 }}>
                  <span style={{ fontSize: "0.75rem", opacity: 0.55, textTransform: "uppercase", letterSpacing: "0.04em", marginRight: 6 }}>End</span>
                  {endOk ? `${format(end as Date, "EEE • MMM d, yy")} • ${format(end as Date, "h:mma")}` : "—"}
                </div>
                {hoursLabel && (
                  <div style={{ marginTop: 4, opacity: 0.65, fontSize: "0.88rem" }}>
                    {hoursLabel} total
                  </div>
                )}
              </>
            )}

            {/* Coverage */}
            {schedJob.JobCancelledByScheduler !== true && (
              <div style={{ marginTop: "20px", opacity: 0.72, fontSize: "0.92rem" }}>
                Coverage: {filled} / {total} filled
              </div>
            )}
          </IonLabel>

          {schedJob.JobCancelledByScheduler === true && (
            <IonText
              slot="end"
              color="danger"
              style={{ fontWeight: 800, letterSpacing: "0.04em" }}
            >
              <span>CANCELLED</span>
            </IonText>
          )}
        </IonItem>

        {/* Pay + paid */}
        <IonItem>
          <IonLabel style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 750, fontSize: "1.06rem", opacity: 0.92, whiteSpace: "normal" }}>
              {schedJob.Pay}
            </div>
          </IonLabel>

          {schedJob.IsPaidJob === true ? (
            <IonButton
              slot="end"
              size="small"
              fill="solid"
              color="medium"
              onClick={onUndoPaid}
              style={{ fontWeight: 800, letterSpacing: "0.04em" }}
            >
              JOB PAID
            </IonButton>
          ) : (
            <IonButton
              slot="end"
              size="small"
              fill="outline"
              color="medium"
              onClick={onMarkPaid}
            >
              Mark Job Paid
            </IonButton>
          )}
        </IonItem>
      </IonList>
    );
  };

  // ---------------------------
  // Top Actions Row (moved INTO Details tab)
  // ---------------------------
  const TopActionsRow: React.FC = () => {
    const canDeleteJob = schedJob.IsPaidJob !== true && schedJob.PaidWerkers !== true;

    return (
      <IonItem
        lines="none"
        style={{
          "--padding-start": "16px",
          "--padding-end": "16px",
          "--inner-padding-end": "0px",
        }}
      >
        <div style={{ display: "flex", gap: 10, width: "100%" }}>
          <IonButton
            fill="solid"
            color="medium"
            onClick={onEdit}
            style={{ flex: 1, margin: 0 }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Edit
              <IonIcon icon={createOutline} />
            </span>
          </IonButton>

          <IonButton
            fill="solid"
            color="medium"
            onClick={onClone}
            style={{ flex: 1, margin: 0 }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Clone
              <IonIcon icon={layersOutline} />
            </span>
          </IonButton>

          <IonButton
            fill="outline"
            color="danger"
            disabled={!canDeleteJob}
            onClick={onDelete}
            style={{ flex: 1, margin: 0 }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Delete
              <IonIcon icon={trashOutline} />
            </span>
          </IonButton>
        </div>
      </IonItem>
    );
  };

  return (
    <React.Fragment key={jobDocId}>
      <CoreSummary />
      <TopActionsRow />

      {/* Location */}
      <SoftDivider mt={10} mb={8} />
      <SectionLabelTight text="Location" mt={0} />

      <IonItem lines="none">
        <IonLabel style={{ userSelect: "text" }}>
          {hasCoordText && (
            <div style={{ fontWeight: 650, opacity: 0.92, whiteSpace: "normal" }}>{resolvedCoords}</div>
          )}

          <div style={{ marginTop: hasCoordText ? 6 : 0, opacity: 0.88, whiteSpace: "normal" }}>{resolvedLocation}</div>
        </IonLabel>

        {/* Right-side vertical buttons */}
        <div
          slot="end"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            alignItems: "flex-end",
          }}
        >
          {/* Maps */}
          <IonButton
            size="small"
            color={hasCoordText ? "secondary" : "medium"}
            onClick={() => {
              if (!hasCoordText) {
                present({ header: "No map data provided", buttons: ["Ok"] });
                return;
              }
              present({
                header: "Open with:",
                buttons: [
                  { text: "Apple Maps", handler: openInAppleMaps },
                  { text: "Google Maps", handler: openInGoogleMaps },
                  "Cancel",
                ],
              });
            }}
          >
            <IonIcon icon={navigate} />
          </IonButton>
        </div>
      </IonItem>

      {/* Notes */}
      <SoftDivider mt={12} mb={8} />
      <NotesSection />
      <SoftDivider mt={10} mb={16} />
    </React.Fragment>
  );
};
